/**
 * KPIBand — bandeau de 3 KPI animés du profil Particulier.
 *
 * Pattern : grille 1/2/3 colonnes avec gap-px (effet "ardoise"), surface
 * stone-50/50, CountUp animée pour les valeurs numériques.
 *
 * Utilisé sous le hero pour donner immédiatement des repères chiffrés
 * (durée, % d'aides, montant cumulable, etc.).
 */

import { Container } from '../../layout/Container';
import { StaggerGrid, StaggerItem } from '../../motion/StaggerGrid';
import { CountUp } from '../../motion/CountUp';
import { cn } from '../../../lib/cn';
import {
  PARTICULIER_SECTION,
  type ParticulierKpi,
} from '../../../config/audience/particulier.ui';

export interface ParticulierKPIBandProps {
  /** 2 à 4 KPI (idéalement 3 pour rester équilibré) */
  items: ParticulierKpi[];
  /** Surcharge éventuelle de la classe section */
  sectionClass?: string;
  /** Colonnes responsives (par défaut sm:grid-cols-3) */
  colsClass?: string;
}

export function ParticulierKPIBand({
  items,
  sectionClass,
  colsClass = 'sm:grid-cols-3',
}: ParticulierKPIBandProps) {
  if (items.length === 0) return null;

  return (
    <section className={cn(PARTICULIER_SECTION.alternateBgCompact, PARTICULIER_SECTION.kpiBand, sectionClass)}>
      <Container>
        <StaggerGrid
          className={cn(
            'grid gap-px overflow-hidden rounded-xl bg-stone-200/60',
            colsClass,
          )}
        >
          {items.map((k) => (
            <StaggerItem key={k.label} className="bg-paper p-5">
              <p className="metric-xl">
                {k.numericValue !== undefined ? (
                  <CountUp end={k.numericValue} suffix={k.suffix} />
                ) : (
                  k.value
                )}
              </p>
              <p className="mt-1.5 text-xs text-stone-700">{k.label}</p>
              {k.source && (
                <p className="mt-0.5 text-[10px] text-stone-400">
                  {k.source.startsWith('Source') ? k.source : `Source : ${k.source}`}
                </p>
              )}
            </StaggerItem>
          ))}
        </StaggerGrid>
      </Container>
    </section>
  );
}
